import React from "react";
import { useLocation, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { ArrowLeft, Download, AlertCircle, ExternalLink } from "lucide-react";
import HighlightedText from "../components/HighlightedText";
import Header from "../components/Header";

const ResultsPage = () => {
  const location = useLocation();
  const result = location.state?.result;

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  if (!result) {
    return (
      <div className="app-shell">
        <div className="print-hidden">
          <Header />
        </div>
        <main className="page-main">
        <div className="max-w-6xl mx-auto px-4 py-16 text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-2xl font-bold mb-4">No Results Found</h1>
          <p className="text-muted-foreground mb-8">
            Please check some text for plagiarism first.
          </p>
          <Link to="/checker">
            <Button>Go to Checker</Button>
          </Link>
        </div>
        </main>
      </div>
    );
  }

  const isAICheck = result.type === 'ai_check';
  
  if (isAICheck) {
    return <AICheckResultsPage result={result} />;
  }

  const { overallSimilarity, matches, analyzedText, isPlagiarism, confidence } = result;

  // Get data from new API response format
  const label = result.label ?? result.heuristic_label ?? null;
  const resultText = result.result_text ?? "";
  const is_paraphrase = label === 4 || resultText === "Diễn đạt lại";
  const is_plagiarism = result.is_plagiarism !== undefined ? result.is_plagiarism : isPlagiarism;
  const confidence_score = result.confidence !== undefined ? result.confidence : confidence;
  const is_legacy_high_confidence =
    !is_paraphrase &&
    !is_plagiarism &&
    Number(confidence_score ?? 0) >= 0.7;
  const sentence_analysis = result.sentence_analysis || [];
  const best_match = result.best_match || null;
  const top_matches = result.top_matches || [];
  const stats = result.stats || {};
  const top_match_score_pct = top_matches.length > 0
    ? Math.max(...top_matches.map((match) => Number(match?.score ?? 0))) * 100
    : null;
  const best_match_score_pct = best_match?.final_score !== undefined
    ? Number(best_match.final_score) * 100
    : null;
  const sentence_similarity_pct = sentence_analysis.length > 0
    ? Math.max(...sentence_analysis.map((item) => Number(item?.similarity ?? item?.confidence ?? 0))) * 100
    : null;
  const suspicious_pct = Number(confidence_score ?? 0) * 100;
  const derived_similarity = top_match_score_pct ?? best_match_score_pct ?? sentence_similarity_pct ?? suspicious_pct;
  const overall_similarity = overallSimilarity ?? derived_similarity ?? 0;

  const getHighlightedText = () => {
    if (matches.length === 0) {
      return analyzedText;
    }

    let highlightedText = "";
    let lastIndex = 0;

    const sortedMatches = [...matches].sort((a, b) => a.startIndex - b.startIndex);

    sortedMatches.forEach((match, index) => {
      highlightedText += analyzedText.substring(lastIndex, match.startIndex);
      
      // Determine color based on similarity
      const similarity = match.similarity || 1.0;
      let colorClass = 'bg-yellow-200 text-yellow-900'; // Low confidence
      
      if (similarity > 0.8) {
        colorClass = 'bg-red-200 text-red-900'; // High confidence
      } else if (similarity > 0.6) {
        colorClass = 'bg-orange-200 text-orange-900'; // Medium confidence
      }
      
      highlightedText += `<mark class="${colorClass} px-1 rounded" data-match="${index}">${match.text}</mark>`;
      lastIndex = match.endIndex;
    });

    highlightedText += analyzedText.substring(lastIndex);
    return highlightedText;
  };

  // Determine plagiarism status message
  const getPlagiarismStatus = () => {
    if (is_paraphrase) {
      return {
        label: "CÓ DẤU HIỆU DIỄN ĐẠT LẠI",
        color: "text-amber-700",
        bgColor: "bg-amber-50",
        borderColor: "border-amber-200"
      };
    }
    if (is_plagiarism || is_legacy_high_confidence) {
      return {
        label: "CÓ DẤU HIỆU ĐẠO VĂN",
        color: "text-red-600",
        bgColor: "bg-red-50",
        borderColor: "border-red-200"
      };
    }
    if (!is_plagiarism) {
      return {
        label: "KHÔNG ĐẠO VĂN",
        color: "text-green-600",
        bgColor: "bg-green-50",
        borderColor: "border-green-200"
      };
    }
    return {
      label: "CÓ DẤU HIỆU ĐẠO VĂN",
      color: "text-red-600",
      bgColor: "bg-red-50",
      borderColor: "border-red-200"
    };
  };

  const status = getPlagiarismStatus();
  const handleExportPdf = () => {
    window.print();
  };

  return (
    <div className="app-shell">
      {/* Navigation */}
      <div className="print-hidden">
        <Header />
      </div>

      {/* Main Content */}
      <main className="page-main">
      <div className="max-w-6xl mx-auto px-4 py-8 print-report-container">
        <div className="print-report">
        <div className="mb-6 print-hidden">
          <Link to="/checker">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Kiểm tra văn bản khác
            </Button>
          </Link>
        </div>

        {/* Results Summary */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Kết quả kiểm tra đạo văn</h1>
          <p className="text-muted-foreground">
            Phân tích hoàn tất - Đây là kết quả kiểm tra đạo văn của bạn
          </p>
        </div>

        {/* Score Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Plagiarism Status Card */}
          <Card className={`p-6 print-card flex items-center justify-center ${status.bgColor} border-2 ${status.borderColor}`}>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground">Trạng thái</div>
              <div className={`text-3xl font-bold ${status.color} mb-2`}>
                {status.label}
              </div>
            </div>
          </Card>

          {/* Confidence Score Card */}
          <Card className="p-6 print-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Điểm Confidence</h3>
              <span className={`text-3xl font-bold ${confidence_score >= 0.6 ? 'text-red-600' : 'text-green-600'}`}>
                {(confidence_score * 100).toFixed(1)}%
              </span>
            </div>
            <Progress value={confidence_score * 100} className="h-3 mb-2" />
            <p className="text-sm text-muted-foreground">
              {confidence_score >= 0.8
                ? "Rất cao - Gần như chắc chắn là đạo văn"
                : confidence_score >= 0.6
                ? "Cao - Có dấu hiệu đạo văn rõ ràng"
                : confidence_score >= 0.4
                ? "Trung bình - Cần xem xét thêm"
                : "Thấp - Văn bản có tính nguyên bản cao"}
            </p>
          </Card>

          {/* Highest segment/source similarity card */}
          <Card className="p-6 print-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Mức tương đồng cao nhất</h3>
              <span className="text-3xl font-bold text-orange-600">
                {overall_similarity.toFixed(1)}%
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              Điểm khớp cao nhất của một đoạn với một nguồn tham khảo. Đây không phải tỷ lệ đạo văn của toàn bài.
            </p>
          </Card>
        </div>

        {/* Top Matches */}
        {top_matches.length > 0 && (
          <Card className="p-6 mb-8 print-card">
            <h2 className="text-2xl font-bold mb-4">Top {top_matches.length} Nguồn Tương Đồng</h2>
            <div className="space-y-4">
              {top_matches.map((match, index) => (
                <div
                  key={index}
                  className="border-l-4 border-orange-500 pl-4 py-2 bg-orange-50 rounded"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-sm">#{index + 1} - {match.doc_id}</span>
                    <span className="text-sm text-orange-600 font-medium">
                      {(match.score * 100).toFixed(1)}% khớp
                    </span>
                  </div>
                  {match.title && (
                    <p className="text-sm mb-2 font-medium">{match.title}</p>
                  )}
                  {match.url && (
                    <a 
                      href={match.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                    >
                      Xem nguồn
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {match.num_chunks} chunks khớp
                  </p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Analyzed Text with Highlights */}
        <Card className="p-6 mb-8 print-card">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-4">
            <h2 className="text-2xl font-bold">Văn Bản Đã Phân Tích</h2>
            <div className="print-hidden">
              <Button className="report-export-button" onClick={handleExportPdf}>
                <Download className="w-4 h-4 mr-2" />
                Xuất Báo Cáo PDF
              </Button>
            </div>
          </div>
          {sentence_analysis.length > 0 ? (
            <HighlightedText sentenceAnalysis={sentence_analysis} legendPosition="top" />
          ) : (
            <div
              className="prose max-w-none text-sm leading-relaxed whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: getHighlightedText() }}
            />
          )}
        </Card>

        {/* Statistics */}
        {stats && Object.keys(stats).length > 0 && (
          <Card className="p-6 print-card">
            <h2 className="text-2xl font-bold mb-4">Thống Kê Phân Tích</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.query_words && (
                <div>
                  <div className="text-sm text-muted-foreground">Số từ</div>
                  <div className="text-2xl font-bold">{stats.query_words}</div>
                </div>
              )}
              {stats.query_chunks && (
                <div>
                  <div className="text-sm text-muted-foreground">Số đoạn</div>
                  <div className="text-2xl font-bold">{stats.query_chunks}</div>
                </div>
              )}
              {stats.corpus_matches && (
                <div>
                  <div className="text-sm text-muted-foreground">Nguồn tìm thấy</div>
                  <div className="text-2xl font-bold">{stats.corpus_matches}</div>
                </div>
              )}
              {stats.total_time && (
                <div>
                  <div className="text-sm text-muted-foreground">Thời gian (giây)</div>
                  <div className="text-2xl font-bold">{stats.total_time.toFixed(2)}s</div>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Old format fallback - Matches Found */}
        {matches && matches.length > 0 && (
          <Card className="p-6 mb-8">
            <h2 className="text-2xl font-bold mb-4">Matches Found ({matches.length})</h2>
            <div className="space-y-4">
              {matches.map((match, index) => (
                <div
                  key={index}
                  className="border-l-4 border-red-500 pl-4 py-2 bg-red-50 rounded"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-sm">Match #{index + 1}</span>
                    <span className="text-sm text-red-600 font-medium">
                      {match.similarity}% similarity
                    </span>
                  </div>
                  <p className="text-sm mb-2">{match.text}</p>
                  <p className="text-xs text-muted-foreground">
                    Source: {match.source}
                  </p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Old format - Analyzed Text (only if no sentence analysis) */}
        {(!sentence_analysis || sentence_analysis.length === 0) && analyzedText && (
          <Card className="p-6">
            <h2 className="text-2xl font-bold mb-4">Analyzed Text</h2>
            <div
              className="prose max-w-none text-sm leading-relaxed whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: getHighlightedText() }}
            />
            {matches && matches.length > 0 && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Highlighted text indicates potential plagiarism matches.
                </p>
              </div>
            )}
          </Card>
        )}
        </div>
      </div>
      </main>
    </div>
  );
};

const AICheckResultsPage = ({ result }) => {
  const {
    combined_prob_ai = 0,
    combined_label = "Nguoi viet",
    sentence_analysis = [],
    analyzedText = "",
    wordCount = 0,
  } = result;

  const totalSegments = sentence_analysis.length;
  const suspiciousSegments = sentence_analysis.filter((sent) => sent.confidence >= 0.6).length;
  const humanSegments = Math.max(totalSegments - suspiciousSegments, 0);
  const aiSegmentRatio = totalSegments > 0 ? (suspiciousSegments / totalSegments) * 100 : 0;
  const avgSegmentConfidence = totalSegments > 0
    ? sentence_analysis.reduce((sum, sent) => sum + Number(sent.confidence || 0), 0) / totalSegments
    : Number(result?.stats?.avg_confidence ?? 0);

  const getAILabelColor = (label) => {
    switch (label) {
      case "Nguoi viet":
        return { color: "text-green-600", bgColor: "bg-green-50", borderColor: "border-green-200" };
      case "Nghi van":
        return { color: "text-yellow-600", bgColor: "bg-yellow-50", borderColor: "border-yellow-200" };
      case "Co dau hieu AI":
        return { color: "text-orange-600", bgColor: "bg-orange-50", borderColor: "border-orange-200" };
      case "AI viet":
        return { color: "text-red-600", bgColor: "bg-red-50", borderColor: "border-red-200" };
      default:
        return { color: "text-gray-600", bgColor: "bg-gray-50", borderColor: "border-gray-200" };
    }
  };

  const labelStyle = getAILabelColor(combined_label);

  const getAISummary = () => {
    if (combined_prob_ai >= 0.8) {
      return {
        headline: "Rất cao - có thể là AI viết",
        subtitle: "Rất có thể do AI tạo ra",
        progressText: "Rất cao - Gần như chắc chắn do AI tạo",
      };
    }
    if (combined_prob_ai >= 0.6) {
      return {
        headline: "Cao - có dấu hiệu AI rõ ràng",
        subtitle: "Nội dung có nhiều tín hiệu gần với văn bản AI",
        progressText: "Cao - Có dấu hiệu AI rõ ràng",
      };
    }
    if (combined_prob_ai >= 0.4) {
      return {
        headline: "Trung bình - nội dung đáng nghi",
        subtitle: "Có một số đoạn mang đặc trưng văn bản AI",
        progressText: "Trung bình - Cần xem xét thêm",
      };
    }
    return {
      headline: "Thấp - nghiêng về người viết",
      subtitle: "Phần lớn tín hiệu cho thấy văn bản do người viết",
      progressText: "Thấp - Văn bản có tính người viết cao",
    };
  };

  const getSegmentStyle = (confidence) => {
    if (confidence >= 0.8) {
      return "bg-red-100 text-red-900 print-highlight-red";
    }
    if (confidence >= 0.6) {
      return "bg-orange-100 text-orange-900 print-highlight-orange";
    }
    if (confidence >= 0.4) {
      return "bg-yellow-100 text-yellow-900 print-highlight-yellow";
    }
    return "bg-transparent text-foreground";
  };

  const summary = getAISummary();
  const handleExportPdf = () => {
    window.print();
  };

  return (
    <div className="app-shell">
      <div className="print-hidden">
        <Header />
      </div>

      <main className="page-main">
      <div className="max-w-6xl mx-auto px-4 py-8 print-report-container">
        <div className="print-report">
        <div className="mb-6 print-hidden">
          <Link to="/checker">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Kiểm tra văn bản khác
            </Button>
          </Link>
        </div>

        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Kết Quả Kiểm Tra AI</h1>
          <p className="text-muted-foreground">
            Phân tích hoàn tất - Đây là kết quả kiểm tra AI của bạn
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card className={`p-6 print-card ${labelStyle.bgColor} border-2 ${labelStyle.borderColor}`}>
            <div className="text-center">
              <div className={`text-3xl font-bold ${labelStyle.color} mb-2`}>
                {summary.headline}
              </div>
              <div className="text-base text-muted-foreground mb-4">{summary.subtitle}</div>
              <div className="text-sm text-muted-foreground">Nhãn phát hiện AI: {combined_label}</div>
            </div>
          </Card>

          <Card className="p-6 print-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Điểm AI toàn văn bản</h3>
              <span className={`text-3xl font-bold ${combined_prob_ai >= 0.6 ? 'text-red-600' : 'text-green-600'}`}>
                {(combined_prob_ai * 100).toFixed(1)}%
              </span>
            </div>
            <Progress value={combined_prob_ai * 100} className="h-3 mb-2" />
            <p className="text-sm text-muted-foreground">{summary.progressText}</p>
          </Card>
        </div>

        <Card className="p-6 mb-8 print-card">
          <h2 className="text-2xl font-bold mb-4">Thống kê phân tích văn bản</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
            <div>
              <div className="text-4xl font-bold text-blue-600">{totalSegments}</div>
              <div className="text-sm text-muted-foreground">Tổng số đoạn</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-red-600">{suspiciousSegments}</div>
              <div className="text-sm text-muted-foreground">Đoạn nghi AI</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-green-600">{humanSegments}</div>
              <div className="text-sm text-muted-foreground">Đoạn do người viết</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-orange-500">{aiSegmentRatio.toFixed(1)}%</div>
              <div className="text-sm text-muted-foreground">Tỷ lệ đoạn nghi AI</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-violet-600">{(avgSegmentConfidence * 100).toFixed(1)}%</div>
              <div className="text-sm text-muted-foreground">Điểm trung bình các đoạn</div>
            </div>
          </div>
          {wordCount > 0 && (
            <div className="mt-6 text-sm text-muted-foreground">
              Số từ đã phân tích: <span className="font-semibold text-foreground">{wordCount}</span>
            </div>
          )}
        </Card>

        {(analyzedText || sentence_analysis.length > 0) && (
          <Card className="p-6 mb-8 print-card">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-4">
              <div className="flex flex-col gap-4">
                <h2 className="text-2xl font-bold">Văn bản được phân tích</h2>
                <div className="flex flex-wrap gap-3 text-sm">
                  <span className="inline-flex items-center gap-2">
                    <span className="h-4 w-4 rounded bg-red-100 border border-red-200" />
                    AI rất cao (&gt;80%)
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <span className="h-4 w-4 rounded bg-orange-100 border border-orange-200" />
                    AI cao (60-80%)
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <span className="h-4 w-4 rounded bg-yellow-100 border border-yellow-200" />
                    AI thấp (40-60%)
                  </span>
                </div>
              </div>
              <div className="print-hidden">
                <Button className="report-export-button" onClick={handleExportPdf}>
                  <Download className="w-4 h-4 mr-2" />
                  Xuất Báo Cáo PDF
                </Button>
              </div>
            </div>
            <div className="prose max-w-none text-sm leading-8 whitespace-pre-wrap rounded-xl border bg-white p-5">
              {sentence_analysis.length > 0 ? (
                <p className="m-0">
                  {sentence_analysis.map((sent, idx) => (
                    <React.Fragment key={idx}>
                      <span className={`rounded px-1 py-0.5 transition-colors ${getSegmentStyle(sent.confidence)}`}>
                        {sent.sentence}
                      </span>
                      {idx < sentence_analysis.length - 1 ? " " : ""}
                    </React.Fragment>
                  ))}
                </p>
              ) : (
                <p>{analyzedText}</p>
              )}
            </div>
          </Card>
        )}

        {sentence_analysis.length > 0 && (
          <Card className="p-6 print-card">
            <h2 className="text-2xl font-bold mb-4">Phân tích từng đoạn</h2>
            <div className="space-y-3">
              {sentence_analysis.map((sent, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded border-l-4 ${
                    sent.confidence >= 0.8
                      ? 'bg-red-50 border-l-red-500'
                      : sent.confidence >= 0.6
                      ? 'bg-orange-50 border-l-orange-500'
                      : sent.confidence >= 0.4
                      ? 'bg-yellow-50 border-l-yellow-500'
                      : 'bg-green-50 border-l-green-500'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-sm font-medium">{sent.sentence}</p>
                    <span className="text-xs font-semibold bg-gray-200 px-2 py-1 rounded">
                      {(sent.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Nhãn: <span className="font-semibold">{sent.label}</span>
                  </p>
                </div>
              ))}
            </div>
          </Card>
        )}
        </div>
      </div>
      </main>
    </div>
  );
};

export default ResultsPage;
